/**
 * Says what the screen is showing and how much it can be trusted.
 *
 * On site this is the difference between "this lineup is finished" and "this
 * phone cannot reach SharePoint". Both look like an empty list; only one of them
 * means stop working.
 */

import { memo } from 'react';
import { freshnessLabel, needsWarning, type Loaded } from '../data/load.js';

export interface FreshnessProps {
  loaded: Loaded<unknown>;
  onRetry?: () => void;
}

export const Freshness = memo(function Freshness({ loaded, onRetry }: FreshnessProps) {
  // Nothing to say when the data is live: a banner that is always there stops
  // being read, and then it is not there when it matters.
  if (loaded.freshness === 'live' && !loaded.rejected?.length) return null;

  const warn = needsWarning(loaded.freshness);
  return (
    <div className={warn ? 'freshness warn' : 'freshness'} role="status" data-testid="freshness">
      <span>{freshnessLabel(loaded)}</span>
      {loaded.rejected?.length ? (
        // Rows the server sent that could not be read. Silence here means the
        // register quietly shrinks and nobody knows which check went missing.
        <span data-testid="rejected">
          {loaded.rejected.length} row{loaded.rejected.length === 1 ? '' : 's'} could not be read
        </span>
      ) : null}
      {onRetry && loaded.freshness !== 'cached' ? (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
});
